import React, { useMemo } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  TooltipItem,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";
import { format } from "date-fns";
import {
  DateValue,
  generateDateRange,
  getDayOfWeek,
  getXAxisLabels,
  isLongDateRange,
} from "~/shared/lib/analytics-utils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

export interface SeriesPoint {
  date: string;
  value: number;
}

interface ChartSeries {
  label: string;
  perDay: SeriesPoint[];
  color?: string;
  fill?: boolean;
}

interface Props {
  series: ChartSeries[];
  startDate: DateValue;
  endDate: DateValue;
  isLoading?: boolean;
  valueSuffix?: string;
  formatValue?: (value: number) => string;
  /** Period total with its unit (e.g. "132 смен") — shown as the tooltip's "Всего" line. */
  total?: string;
}

// Sums per-day points into the dense day (or week, for ranges over a month) buckets
// of the visible range — the API returns sparse days.
function bucketValues(perDay: SeriesPoint[], dateRange: DateValue[], weekly: boolean): number[] {
  const byDate = new Map<string, number>();
  perDay.forEach((p) => byDate.set(p.date, (byDate.get(p.date) ?? 0) + p.value));

  const daily = dateRange.map((date) => byDate.get(date.toString()) ?? 0);
  if (!weekly) return daily;

  const weeklyData: number[] = [];
  for (let i = 0; i < daily.length; i += 7) {
    weeklyData.push(daily.slice(i, i + 7).reduce((sum, v) => sum + v, 0));
  }
  return weeklyData;
}

export const AnalyticsLineChart: React.FC<Props> = ({
  series,
  startDate,
  endDate,
  isLoading,
  valueSuffix,
  formatValue,
  total,
}) => {
  const t = useT();
  const dateLocale = useDateFnsLocale();

  const dateRange = useMemo(() => generateDateRange(startDate, endDate), [startDate, endDate]);
  const weekly = useMemo(() => isLongDateRange(startDate, endDate), [startDate, endDate]);

  // Short ranges: two-line ticks — day number over weekday. Long ranges: week spans.
  const labels = useMemo<(string | string[])[]>(() => {
    if (weekly) return getXAxisLabels(dateRange, weekly, t, dateLocale);
    return dateRange.map((d) => {
      const jsDate = new Date(d.year, d.month - 1, d.day);
      const weekday = format(jsDate, "EEEEEE", { locale: dateLocale });
      return [String(d.day), weekday.charAt(0).toUpperCase() + weekday.slice(1)];
    });
  }, [dateRange, weekly, t, dateLocale]);

  const lastIndex = weekly ? Math.ceil(dateRange.length / 7) - 1 : dateRange.length - 1;

  const datasets = useMemo(
    () =>
      series.map((s) => ({
        label: s.label,
        data: bucketValues(s.perDay, dateRange, weekly),
        borderColor: s.color ?? "#0066FF",
        backgroundColor: s.fill === false ? "transparent" : "#E5EFFF",
        fill: s.fill !== false,
        tension: 0.4,
        pointRadius: (ctx: { dataIndex: number }) => (ctx.dataIndex === lastIndex ? 4 : 0),
        pointHoverRadius: 4,
        pointBackgroundColor: s.color ?? "#0066FF",
        borderWidth: 2,
        hoverBorderWidth: 2,
      })),
    [series, dateRange, weekly, lastIndex],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: { padding: { left: 10, right: 15, top: 10, bottom: 10 } },
      interaction: { mode: "index" as const, axis: "x" as const, intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          backgroundColor: "white",
          titleColor: "#111827",
          bodyColor: "#0066FF",
          footerColor: "#0066FF",
          bodyFont: { weight: "bold" as const },
          footerFont: { weight: "normal" as const },
          borderColor: "#e5e7eb",
          borderWidth: 1,
          cornerRadius: 14,
          caretSize: 0,
          padding: { top: 8, bottom: 8, left: 14, right: 14 },
          displayColors: series.length > 1,
          callbacks: {
            title: (items: TooltipItem<"line">[]) => {
              if (!items.length) return "";
              const index = items[0].dataIndex;
              if (weekly || index >= dateRange.length) return items[0].label;
              const d = dateRange[index];
              const formatted = format(new Date(d.year, d.month - 1, d.day), "d MMM yyyy", {
                locale: dateLocale,
              });
              return formatted.charAt(0).toUpperCase() + formatted.slice(1);
            },
            label: (item: TooltipItem<"line">) => {
              const value = item.raw as number;
              const formatted = formatValue ? formatValue(value) : String(Math.round(value));
              const suffix = valueSuffix ? ` ${valueSuffix}` : "";
              return series.length > 1
                ? `${item.dataset.label}: ${formatted}${suffix}`
                : `${formatted}${suffix}`;
            },
            footer: () =>
              total ? `${t(Namespace.Analytics, "tooltip.total")} ${total}` : "",
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            padding: 8,
            maxRotation: 0,
            color: (ctx: { index: number }) => {
              if (weekly) return "black";
              if (ctx.index < dateRange.length) {
                const day = getDayOfWeek(dateRange[ctx.index], t);
                if (day === t(Namespace.Analytics, "date.Sunday")) return "red";
              }
              return "black";
            },
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: (ctx: { tick: { value: number } }) =>
              ctx.tick.value === 0 ? "#E5E7EB" : "#F3F4F6",
          },
          border: { display: false },
          ticks: { padding: 8, precision: 0, color: "#727986" },
        },
      },
    }),
    [series.length, weekly, dateRange, t, dateLocale, valueSuffix, formatValue, total],
  );

  return (
    <div className="relative h-full min-h-[220px] border border-gray-100 rounded-xl">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-80">
          <div className="text-center">
            <div className="mx-auto mb-2 h-6 w-6 animate-spin border border-blue-500 border-r-transparent rounded-full" />
            <span className="text-sm text-gray-500">{t(Namespace.Analytics, "table.loading")}</span>
          </div>
        </div>
      )}
      <Line data={{ labels, datasets }} options={options} />
    </div>
  );
};
